import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/chalkApi";
import { ChalkLogoLockup } from "../components/chalk/Logo";

/**
 * Password reset page — destination of the email link.
 *
 * Flow:
 *   1. User taps link from email → `/reset?token=XYZ`
 *   2. We read the token from the query string
 *   3. User chooses a new password (with strength check)
 *   4. POST /auth/reset with { token, new_password }
 *   5. On success, redirect to /login with a confirmation toast
 *
 * Failure modes handled:
 *   - Missing token in URL → "This link looks broken." with link back to /forgot
 *   - Expired or already-used token → server returns INVALID_TOKEN, we surface a clear message
 *   - Weak password → server returns WEAK_PASSWORD, inline error
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!token) setServerError("This link looks broken — no reset token found.");
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    setServerError(null);
    if (!token) return;
    if (pw.length < 8) {
      setServerError("Choose a password that's at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setServerError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.reset(token, pw);
      setDone(true);
      toast.success("Password reset. Sign in with your new password.");
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (err) {
      setServerError(err.message || "We couldn't reset your password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="reset-password-page"
      className="chalk-min-h-screen"
      style={{
        background: "var(--chalk-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div className="chalk-fadeup" style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <ChalkLogoLockup size={32} color="var(--chalk-board)" />
        </div>

        {done ? (
          <div className="chalk-card" data-testid="reset-done" style={{ borderColor: "rgba(46,125,82,0.30)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--green-mid)" }}>
              <CheckCircle2 size={22} aria-hidden="true" />
              <div style={{ fontSize: 16, fontWeight: 600 }}>Password reset.</div>
            </div>
            <div style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 8 }}>
              Redirecting to sign in…
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display" style={{ fontSize: 36, margin: 0, color: "var(--chalk-board)", lineHeight: 0.95 }}>
              Choose a new password.
            </h1>
            <p style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 8 }}>
              At least 8 characters. Make it one you'll remember.
            </p>

            <form onSubmit={onSubmit} style={{ marginTop: 22, display: "grid", gap: 12 }}>
              <PasswordField
                label="New password"
                value={pw}
                onChange={setPw}
                show={showPw}
                onToggleShow={() => setShowPw((v) => !v)}
                autoFocus
                testId="reset-pw1"
              />
              <PasswordField
                label="Confirm password"
                value={pw2}
                onChange={setPw2}
                show={showPw}
                onToggleShow={() => setShowPw((v) => !v)}
                testId="reset-pw2"
              />

              {serverError && (
                <div
                  data-testid="reset-error"
                  role="alert"
                  style={{
                    fontSize: 13, color: "#a00031",
                    background: "rgba(160,0,49,0.06)",
                    padding: "10px 12px", borderRadius: 9,
                    border: "0.5px solid rgba(160,0,49,0.18)",
                  }}
                >
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                className="chalk-btn-athletic"
                disabled={loading || !token}
                data-testid="reset-submit"
                style={{ opacity: (loading || !token) ? 0.6 : 1 }}
              >
                {loading ? "Resetting…" : (<>Reset password <ArrowRight size={16} aria-hidden="true" /></>)}
              </button>

              <Link
                to="/login"
                data-testid="reset-back-signin"
                style={{
                  color: "var(--chalk-muted)", fontSize: 13,
                  textDecoration: "none", textAlign: "center", marginTop: 4,
                }}
              >
                Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// Reusable password input row. Eye toggle is parent-controlled so both
// inputs reveal/hide together (the user is confirming the same value).
function PasswordField({ label, value, onChange, show, onToggleShow, autoFocus, testId }) {
  return (
    <div style={{ position: "relative" }}>
      <span
        style={{
          position: "absolute", left: 14, top: "50%",
          transform: "translateY(-50%)", color: "var(--chalk-muted-2)",
          pointerEvents: "none",
        }}
      >
        <Lock size={16} aria-hidden="true" />
      </span>
      <input
        type={show ? "text" : "password"}
        className="chalk-input"
        placeholder={label}
        value={value}
        autoFocus={autoFocus}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        aria-label={label}
        style={{ paddingLeft: 38, paddingRight: 38 }}
      />
      <button
        type="button"
        onClick={onToggleShow}
        data-testid={`${testId}-toggle`}
        style={{
          position: "absolute", right: 12, top: "50%",
          transform: "translateY(-50%)",
          background: "transparent", border: "none",
          color: "var(--chalk-muted-2)", cursor: "pointer",
        }}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}
